import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Search,
  Send,
  Paperclip,
  MoreVertical,
  Circle,
  CheckCircle2,
  Clock,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';

const MessengerStyleSupport = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('open');
  
  // Stats
  const [stats, setStats] = useState({
    open: 0,
    assigned: 0,
    resolved: 0,
    closed: 0
  });

  const messagesEndRef = useRef(null);
  const searchInputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  useEffect(() => {
    fetchConversations();
    fetchStats();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchConversations();
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  // Filter conversations based on search
  useEffect(() => {
    if (!searchQuery) {
      setFilteredConversations(conversations);
    } else {
      const filtered = conversations.filter(conv => {
        const searchLower = searchQuery.toLowerCase();
        // Search by user ID, subject, or UUID
        return (
          conv.user_id?.toString().includes(searchLower) ||
          conv.subject?.toLowerCase().includes(searchLower) ||
          conv.conversation_uuid?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredConversations(filtered);
    }
  }, [conversations, searchQuery]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const status = activeTab === 'all' ? '' : activeTab.toUpperCase();
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations?status=${status}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const statuses = ['open', 'assigned', 'resolved', 'closed'];
      const statsData = {};
      
      for (const status of statuses) {
        const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations?status=${status.toUpperCase()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          statsData[status] = data.total || 0;
        }
      }
      
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: messageInput.trim()
        })
      });
      
      if (response.ok) {
        setMessageInput('');
        // Refresh conversation details
        const detailResponse = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations/${selectedConversation.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (detailResponse.ok) {
          const conversation = await detailResponse.json();
          setSelectedConversation(conversation);
          // Update conversation in list
          setConversations(prev => prev.map(conv => 
            conv.id === conversation.id ? conversation : conv
          ));
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = async (conversation) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations/${conversation.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const conv = await response.json();
        setSelectedConversation(conv);
        // Mark as read (update in list)
        setConversations(prev => prev.map(c => 
          c.id === conversation.id ? { ...c, unread_count: 0 } : c
        ));
      }
    } catch (error) {
      console.error('Failed to fetch conversation details:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN': return <Circle className="w-3 h-3 fill-yellow-500 text-yellow-500" />;
      case 'ASSIGNED': return <Circle className="w-3 h-3 fill-blue-500 text-blue-500" />;
      case 'RESOLVED': return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case 'CLOSED': return <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />;
      default: return <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'text-yellow-600';
      case 'ASSIGNED': return 'text-blue-600';
      case 'RESOLVED': return 'text-green-600';
      case 'CLOSED': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  const tabs = [
    { id: 'open', label: 'Open', count: stats.open },
    { id: 'assigned', label: 'Assigned', count: stats.assigned },
    { id: 'resolved', label: 'Resolved', count: stats.resolved },
    { id: 'closed', label: 'Closed', count: stats.closed },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Live Support</h1>
              <p className="text-sm text-gray-500">Manage customer conversations</p>
            </div>
          </div>
          
          {/* Stats Pills */}
          <div className="flex items-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - User List */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by user ID, subject..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                <MessageSquare className="w-8 h-8 mb-2" />
                <p className="text-sm">No conversations found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
                      selectedConversation?.id === conv.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status Indicator */}
                      <div className="mt-1">{getStatusIcon(conv.status)}</div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* User Info */}
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-gray-900 truncate">
                            User #{conv.user_id}
                          </p>
                          <span className="text-xs text-gray-500">
                            {formatDate(conv.updated_at)}
                          </span>
                        </div>
                        
                        {/* Subject */}
                        <p className="text-sm text-gray-900 truncate mb-1">
                          {conv.subject || 'No subject'}
                        </p>
                        
                        {/* Meta Info */}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>ID: {conv.conversation_uuid}</span>
                          <span className={getStatusColor(conv.status)}>
                            {conv.status}
                          </span>
                          {conv.unread_count > 0 && (
                            <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                              {conv.unread_count} new
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Chat Window */}
        {selectedConversation ? (
          <div className="flex-1 flex flex-col bg-white">
            {/* Chat Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    User #{selectedConversation.user_id}
                  </h2>
                  <div className="flex items-center gap-3 mt-1">
                    <p className="text-sm text-gray-500">
                      ID: {selectedConversation.conversation_uuid}
                    </p>
                    <span className={`text-sm font-medium ${getStatusColor(selectedConversation.status)}`}>
                      {selectedConversation.status}
                    </span>
                  </div>
                </div>
                
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {selectedConversation.messages?.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.sender_type === 'USER' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-lg px-4 py-2 rounded-2xl ${
                      message.sender_type === 'USER'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm leading-relaxed">{message.message}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender_type === 'USER' ? 'text-indigo-200' : 'text-gray-500'
                      }`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message Input */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-end gap-3">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex-1">
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Type your message..."
                    rows={1}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={sending}
                  />
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!messageInput.trim() || sending}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select a conversation
              </h3>
              <p className="text-gray-500">
                Choose a conversation from the list to start messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessengerStyleSupport;
